import throttle from 'lodash/throttle';
import { useCallback, useEffect, useRef } from 'react';

type Options = {
  checkHasMore?: (data: Record<string, any>[]) => boolean,
  deps?: unknown[],
  getOffset?: () => number,
  threshold?: number,
}

export const useInfiniteScroll = (
  callback: (offset: number) => Promise<Record<string, any>[]>,
  {
    checkHasMore = d => !!d.length,
    deps = [],
    getOffset = () => 0,
    threshold = 200,
  }: Options = {}
) => {
  const isFetching = useRef(false);
  const hasMore = useRef(true);
  const fetchedOffsets = useRef<Record<number, boolean>>({});

  const fetchData = useCallback(
    async (offset: number) => {
      if (fetchedOffsets.current[offset]) {
        return;
      }

      isFetching.current = true;
      fetchedOffsets.current[offset] = true;

      try {
        const data = await callback(offset);

        hasMore.current = checkHasMore(data);
      } catch (e) {
        console.error(e);
      } finally {
        isFetching.current = false;
      }
    },
    [callback, isFetching, hasMore]
  );

  const handleScroll = useCallback(
    throttle(async () => {
      if (isFetching.current || !hasMore.current) {
        return;
      }

      const element = document.documentElement;

      if (element.scrollHeight >= element.scrollTop + threshold + element.offsetHeight) {
        return;
      }

      fetchData(getOffset());
    }, 200),
    [fetchData, getOffset, hasMore, isFetching]
  );

  useEffect(() => {
    fetchedOffsets.current = {};
    fetchData(0);
  }, deps);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);
};
